/** Minutes-long, GET-only, image/SSE-scoped tokens: `<img>` and EventSource can't send a
 *  header, and a query param leaks into logs, so the 24h session JWT must never go there. */

const TOKEN_STORAGE_KEY = 'chub-auth-token';
const SKEW_MS = 30_000; // refresh this long before expiry
const RETRY_BASE_MS = 30_000;
const RETRY_MAX_MS = 300_000;

let cached = null; // { token: string, expMs: number }
let inflight = null;
let refreshTimer = null;
// Bumped by clearStreamToken: a mint that resolves after a clear must not restore
// `cached`, notify, re-arm the refresh timer, or null a newer `inflight`.
let mintGeneration = 0;
// null = unknown, true = auth on (token required), false = auth not configured
// (the /stream-token route is open, so image/SSE URLs need no token at all).
let authConfigured = null;
// Gate re-entry after a failed mint — see the loop note in ensureStreamToken.
let retryAfterMs = 0;
let retryDelayMs = RETRY_BASE_MS;
// The JWT a terminal 401 was seen on; retrying can't succeed until it changes.
let rejectedJwt = null;
const listeners = new Set();

function fullToken() {
    try {
        return localStorage.getItem(TOKEN_STORAGE_KEY) || '';
    } catch {
        return '';
    }
}

function fresh() {
    return cached && cached.expMs - Date.now() > SKEW_MS ? cached.token : '';
}

function notify() {
    listeners.forEach(cb => cb());
}

/** Subscribe to stream-token changes; returns an unsubscribe fn. */
export function subscribeStreamToken(cb) {
    listeners.add(cb);
    return () => listeners.delete(cb);
}

/** The currently cached token ('' if none yet / cleared). */
export function streamTokenSnapshot() {
    return (cached && cached.token) || '';
}

/** True only once auth is confirmed OFF, so callers build token-less URLs. False while
 *  unknown — a placeholder beats firing a token-less request that 401s. */
export function streamAuthDisabled() {
    return authConfigured === false;
}

/** Mirrors core.js's 401 handling — this module uses a raw fetch, so it never reaches
 *  that handler, and a dead session must land on /login. */
function handleAuthFailure(status, body) {
    const code = body?.error_code;
    if (status !== 401 || (code !== 'AUTH_REQUIRED' && code !== 'AUTH_TOKEN_INVALID')) {
        return;
    }
    rejectedJwt = fullToken();
    try {
        localStorage.removeItem(TOKEN_STORAGE_KEY);
    } catch {
        /* noop */
    }
    if (window.location.pathname !== '/login') {
        window.location.href = '/login';
    }
}

/** True while the last failure was a handled 401 with no new credential since.
 *  Without this the /login timers mint forever against a cleared session. */
function terminalAuthFailure() {
    if (rejectedJwt === null) return false;
    const jwt = fullToken();
    if (jwt && jwt !== rejectedJwt) {
        rejectedJwt = null;
        return false;
    }
    return true;
}

function scheduleProactiveRefresh(ttlMs) {
    if (refreshTimer) clearTimeout(refreshTimer);
    // Refresh SKEW before expiry so the token never actually lapses. Force a
    // refetch by clearing `cached` first (ensureStreamToken short-circuits while
    // fresh, so the old interval-based refresh never actually renewed anything).
    const delay = Math.max(5_000, ttlMs - SKEW_MS);
    refreshTimer = setTimeout(() => {
        cached = null;
        ensureStreamToken();
    }, delay);
}

// One bounded retry after a failed mint, so a transient backend blip self-heals
// instead of leaving images token-less until the next unrelated re-render.
function scheduleRetry(delayMs) {
    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => {
        retryAfterMs = 0;
        ensureStreamToken();
    }, delayMs);
}

/** Fetch (or reuse) a valid stream token. Concurrent callers share one request. */
export async function ensureStreamToken() {
    const current = fresh();
    if (current) return current;
    // Auth confirmed off: the token is always empty and the route is open, so
    // stop re-hitting /stream-token on every render — there is nothing to mint.
    if (authConfigured === false) return '';
    // A failed mint must NOT re-arm on the next render: notify() re-renders every
    // subscriber, each rebuilt image URL calls streamTokenParam() -> back in here,
    // and without this gate that is an unbounded 401 loop (~46 req/s).
    if (Date.now() < retryAfterMs) return '';
    if (terminalAuthFailure()) return '';
    if (!inflight) {
        const generation = mintGeneration;
        const jwt = fullToken();
        inflight = fetch('/api/auth/stream-token', {
            method: 'POST',
            headers: jwt ? { Authorization: `Bearer ${jwt}` } : {},
        })
            .then(r => {
                // Cheap early-out only; r.json() below is async, so the decisive
                // check must sit adjacent to the side effect it guards.
                if (generation !== mintGeneration) return null;
                if (r.ok) return r.json();
                return r
                    .json()
                    .catch(() => null)
                    .then(body => {
                        // Re-checked after the parse: handleAuthFailure removes the
                        // JWT and redirects, so a stale 401 must never reach it.
                        if (generation !== mintGeneration) return null;
                        handleAuthFailure(r.status, body);
                        return null;
                    });
            })
            // MUST sit before the classification below: a network-level reject
            // would otherwise skip it for the trailing catch, leaving the retry
            // gate open and the loop free to re-arm during an outage.
            .catch(() => null)
            .then(d => {
                // Re-checked: the retry gate and backoff below are side effects too.
                if (generation !== mintGeneration) return '';
                const token = d?.data?.token || '';
                const ttl = (d?.data?.expires_in || 0) * 1000;
                // A successful (200) response resolves the auth state: a
                // non-empty token means auth is on; an empty token means auth
                // is not configured (the route is open). A failed response
                // (null) is an auth-on 401/expired-JWT case, so leave the state
                // unchanged rather than mislabelling it as "not configured".
                if (d) authConfigured = token !== '';
                if (!d) {
                    // Back off, and do NOT notify — a re-render is what re-arms
                    // the loop, and nothing changed for subscribers anyway.
                    // A handled 401 is terminal: no timer, it can't succeed.
                    if (!terminalAuthFailure()) {
                        retryAfterMs = Date.now() + retryDelayMs;
                        scheduleRetry(retryDelayMs);
                        retryDelayMs = Math.min(retryDelayMs * 2, RETRY_MAX_MS);
                    }
                    return '';
                }
                retryAfterMs = 0;
                retryDelayMs = RETRY_BASE_MS;
                rejectedJwt = null;
                cached = token ? { token, expMs: Date.now() + ttl } : null;
                if (cached) scheduleProactiveRefresh(ttl);
                notify();
                return token;
            })
            .catch(() => '')
            .finally(() => {
                // Only clear our own: a stale chain would otherwise null a mint
                // started after the clear, stranding its callers.
                if (generation === mintGeneration) inflight = null;
            });
    }
    return inflight;
}

/** Synchronous accessor for building `<img src>` during render; kicks off a refresh
 *  when missing or stale. useStreamToken re-renders once one arrives. */
export function streamTokenParam() {
    const current = fresh();
    if (!current && authConfigured !== false) ensureStreamToken();
    return current;
}

export function clearStreamToken() {
    // Invalidate any mint already in flight; nulling `inflight` alone does not stop it.
    mintGeneration += 1;
    cached = null;
    inflight = null;
    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = null;
    retryAfterMs = 0;
    retryDelayMs = RETRY_BASE_MS;
    rejectedJwt = null;
    // The session changed, so the login may have been turned off or back on: re-learn it.
    authConfigured = null;
    notify();
}
